import { getValueForEnvironment } from '@servicetitan/web-components';

export type TaskHubEnvironment = 'test' | 'dev' | 'qa' | 'stage' | 'prod' | 'next';
export type MfeTag = 'dev' | 'qa' | 'stage' | 'prod' | 'next';

const TASK_HUB_ENVIRONMENT_VALUES: readonly TaskHubEnvironment[] = [
    'test',
    'dev',
    'qa',
    'stage',
    'prod',
    'next',
];

function isTaskHubEnvironmentString(value: string): value is TaskHubEnvironment {
    return (TASK_HUB_ENVIRONMENT_VALUES as readonly string[]).includes(value);
}

export type ResolveTaskHubEnvironmentFromMfeContextResult =
    { ok: true; environment: TaskHubEnvironment } | { ok: false };

/**
 * Resolves Task Hub environment for MFE embedding.
 * When the host omits `environment`, the value is inferred from the page hostname.
 * When the host passes `environment`, it must be a known {@link TaskHubEnvironment} string
 * (e.g. not `"integration"`).
 */
export function resolveTaskHubEnvironmentFromMfeContext(
    explicitFromHost: unknown,
    hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): ResolveTaskHubEnvironmentFromMfeContextResult {
    if (explicitFromHost === undefined || explicitFromHost === null || explicitFromHost === '') {
        return { ok: true, environment: getTaskHubEnvironment(hostname) };
    }
    if (typeof explicitFromHost !== 'string') {
        return { ok: false };
    }
    if (!isTaskHubEnvironmentString(explicitFromHost)) {
        return { ok: false };
    }
    return { ok: true, environment: explicitFromHost };
}

/**
 * Returns true if the hostname is a local/dev address (localhost, 127.*, 192.168.*, 10.*, ::1, [::1], 172.16-31.*)
 */
function isLocalDevHost(hostname: string): boolean {
    return (
        hostname.includes('localhost') ||
        hostname === '::1' ||
        hostname.startsWith('127.') ||
        hostname.startsWith('192.168.') ||
        /^\[::1\]$/.test(hostname) ||
        /^10\./.test(hostname) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname) ||
        hostname === 'app-web'
    );
}

/**
 * Resolves a TaskHubEnvironment from a URL hostname by matching known BFF and local patterns.
 * Returns null if no pattern matches.
 */
export function getTaskHubEnvironmentFromUrlHost(hostname: string): TaskHubEnvironment | null {
    if (isLocalDevHost(hostname)) {
        return 'dev';
    }

    if (hostname.endsWith('.next.prod.task-hub.srv.servicetitan.com')) {
        return 'next';
    }

    if (hostname.endsWith('.prod.task-hub.srv.servicetitan.com')) {
        return 'prod';
    }

    if (hostname.endsWith('.stage.task-hub.srv.servicetitan.com')) {
        return 'stage';
    }

    if (hostname.endsWith('.qa.devtest.task-hub.srv.st.dev')) {
        return 'qa';
    }

    if (hostname.endsWith('.sandbox.devtest.task-hub.srv.st.dev')) {
        return 'dev';
    }

    return null;
}

/**
 * Detects the Task Hub environment by probing BFF hostname patterns first,
 * then falling back to platform detection for monolith-hosted pages.
 *
 * @param hostname - The hostname to check (defaults to window.location.hostname)
 */
export function getTaskHubEnvironment(
    hostname: string = window.location.hostname
): TaskHubEnvironment {
    return (
        getTaskHubEnvironmentFromUrlHost(hostname) ??
        (getValueForEnvironment({
            dev: 'dev',
            qa: 'qa',
            stage: 'stage',
            go: 'prod',
            next: 'next',
        }) as TaskHubEnvironment | undefined) ??
        'dev'
    );
}

/**
 * Maps a TaskHubEnvironment to the corresponding unpkg MFE tag.
 */
export function getTaskHubEnvironmentMfeTag(environment: TaskHubEnvironment): MfeTag {
    const tags: Record<TaskHubEnvironment, MfeTag> = {
        test: 'dev',
        dev: 'dev',
        qa: 'qa',
        stage: 'stage',
        prod: 'prod',
        next: 'next',
    };
    return tags[environment];
}

export function buildBffBaseUrl(
    environment: TaskHubEnvironment,
    logicalEnvironment?: string | null
): string {
    const base = getTaskHubEnvironmentBaseUrl(environment);
    if (!base || !logicalEnvironment) {
        return base;
    }
    // Reject anything outside the canonical logical-environment charset before it lands in the URL host.
    if (!/^[a-zA-Z0-9_-]+$/.test(logicalEnvironment)) {
        return base;
    }
    return base.replace('://', `://${logicalEnvironment}.`);
}

export function getTaskHubEnvironmentBaseUrl(environment: TaskHubEnvironment): string {
    // When Task Hub is loaded as MFE from app-web, use special Task Hub BFF
    if (typeof window !== 'undefined' && window.location.hostname === 'app-web') {
        return 'https://task-hub.localhost';
    }

    // When running on a recognized non-local BFF host (incl. sandbox), API is same-origin
    if (
        typeof window !== 'undefined' &&
        !isLocalDevHost(window.location.hostname) &&
        getTaskHubEnvironmentFromUrlHost(window.location.hostname) !== null
    ) {
        return '';
    }

    const urls: Record<TaskHubEnvironment, string> = {
        test: '',
        dev: '',
        qa: 'https://bff.wus201.qa.devtest.task-hub.srv.st.dev',
        stage: 'https://bff.wus201.stage.stage.task-hub.srv.servicetitan.com',
        prod: 'https://bff.wus201.prod.prod.task-hub.srv.servicetitan.com',
        next: 'https://bff.wus201.next.prod.task-hub.srv.servicetitan.com',
    };

    return urls[environment];
}
